import {Column} from 'primereact/column';
import {InputText} from 'primereact/inputtext';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {apiRequest, toast} from '@/Helpers/general';
import {CASE_STATUSES_OPTIONS, DT_REQUESTS_FILTERS, DT_REQUESTS_RELOAD_INTERVAL, DT_ROWS_NO} from '@/Config/constants';
import debounce from 'lodash.debounce';
import DatatableTemplate from '@/Components/Templates/DatatableTemplate';
import {dateBodyTemplate} from '@/Components/Templates/DatatableColumnTemplates';
import {AppContext} from '@/Components/Context/AppContext';
import {classNames} from 'primereact/utils';
import {Button} from 'primereact/button';
import DropdownInputTemplate from '@/Components/Templates/Forms/DropdownInputTemplate';
import CaseModal from '@/Components/Modals/CaseModal';
import {RequestsContext} from '@/Components/Context/RequestsContext';
import {can, cant} from "@/Util/permissionChecks";

/**
 * Requests datatable
 */
const RequestsDatatable = () => {
    const searchRequestRef = useRef();
    const {toastRef, authenticatedUserData, userPreferences, setUserPreferences} = useContext(AppContext);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequests, setSelectedRequests] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(DT_ROWS_NO);
    const [first, setFirst] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchRequestInput, setSearchRequestInput] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState('request_date');
    const [sortOrder, setSortOrder] = useState(-1);
    const [clearSearchIcon, setClearSearchIcon] = useState(false);
    const [currentFilter, setCurrentFilter] = useState(userPreferences && userPreferences.dashboard_filter
        ? userPreferences.dashboard_filter
        : (can('access:supervisor') ? 'today' : 'my-cases')
    );
    const [usersOptions, setUsersOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [chosOptions, setChosOptions] = useState([]);
    const [gtaGroupsOptions, setGtaGroupsOptions] = useState([]);
    const [showCaseModal, setShowCaseModal] = useState(false);
    const [currentCaseId, setCurrentCaseId] = useState();
    const [intervalVar, setIntervalVar] = useState(null);
    const [currentRequest, setCurrentRequest] = useState();

    const getStatusChangedTooltipMessage = (auditMessage: string, date: string) => {
        return `${auditMessage} at ${date}`;
    }

    /**
     * Get requests from the api
     */
    const getRequests = () => {
        const requestParams = {
            page: currentPage + 1,
            limit: rowsPerPage
        };
        setLoading(true);

        // search param
        if (searchRequestInput && searchRequestInput != '') {
            requestParams.search = searchRequestInput;
            setClearSearchIcon(true);
        }

        // hide clear search icon
        if (searchRequestInput == '') {
            setClearSearchIcon(false);
        }

        // sort params
        if (sortField !== null) {
            requestParams.sort_by = sortField;
        }

        if (sortOrder != 0) {
            requestParams.sort_order = sortOrder === -1 ? 'desc' : 'asc';
        }

        // filter params
        requestParams.filter_by = currentFilter;

        apiRequest('get', 'requests', {
            data: requestParams,
            onSuccess: (response) => {
                setLoading(false);

                if (response.result) {
                    setRequests(response.result);
                }

                setTotalRecords(response.pagination?.totalEntries ? response.pagination.totalEntries : 0);
            },
            onFailure: () => {
                setLoading(false);
            }
        });
    };

    /**
     * Search for a request
     *
     * @param {object} e event object containing search data
     */
    const searchRequest = (e) => {
        setSearchRequestInput(e.target.value);
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * Clear the request search
     */
    const clearRequestSearch = () => {
        searchRequestRef.current.value = '';
        setSearchRequestInput('');
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * Debounce the request search function
     */
    const debounceRequestSearch = useMemo(() => {
        return debounce(searchRequest, 500);
    }, []);

    /**
     * On sort column function
     *
     * @param {object} e event object containing sort data
     */
    const onSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * On change page function
     *
     * @param {object} e event object containing current page related data
     */
    const onPageChange = (e) => {
        // console.log(e);
        setCurrentPage(e.page);
        setFirst(e.first);
        setRowsPerPage(e.rows);
    };

    /**
     * Change the main filter
     *
     * @param {string} newFilter new filter value
     */
    const changeFilter = (newFilter) => {
        const copyUserPreferences = {...userPreferences};

        copyUserPreferences.dashboard_filter = newFilter;

        setCurrentFilter(newFilter);
        setUserPreferences(copyUserPreferences);
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * The header of the datatable
     */
    const header = () => {
        return (
            <>
                <span className="p-input-icon-left search-input-wrap">
                    <i className="pi pl-2 pi-search"/>
                    <InputText className="narrow-input pr-5 pl-5" onChange={debounceRequestSearch}
                               ref={searchRequestRef} placeholder="Search claim"/>
                    {clearSearchIcon &&
                        <i className="pi pi-times bg-transparent border-transparent p-button p-button-sm p-button-danger p-button-text p-1"
                           onClick={clearRequestSearch}/>}
                </span>
                {/* <div className="flex justify-content-between align-items-center"> */}
                <div className="filters-wrap flex">
                    {DT_REQUESTS_FILTERS.map((filter) => {
                        return (
                            <div className={classNames({
                                'filter': true,
                                'active': currentFilter == filter.key,
                                'highlight-filter': filter.highlight
                            })}
                                 onClick={() => {
                                     changeFilter(filter.key);
                                 }}
                                 key={filter.key}
                            >
                                <span>{filter.name}</span>
                            </div>
                        );
                    })}
                </div>
                {/* </div> */}
            </>
        );
    };

    /**
     * Status body template
     */
    const statusBodyTemplate = (status) => {
        const statusConfig = CASE_STATUSES_OPTIONS.find((item) => item.label == status?.label);

        if (statusConfig) {
            return (
                <div className="status-wrap" style={{
                    // version 1
                    backgroundColor: statusConfig.newColor,
                    textShadow: '1px 1px 3px ' + (statusConfig.alternativeTextShadow1 || '#555555')
                }}>
                    <span>{status?.label}</span>
                </div>
            );
        } else {
            return <span>{status?.label}</span>;
        }
    };

    /**
     * SLA column template
     */
    const slaBodyTemplate = (data) => {
        return (
            <div className={classNames({'sla-wrap': true, 'expired': data.expired_sla})}>
                <i className={'pi ' + (data.expired_sla ? 'pi-times-circle' : 'pi-check-circle')}></i>
            </div>
        );
    };

    /**
     * Assign a user to a request
     *
     * @param {string} name the name of the dropdown
     * @param {integer} value the selected value from the dropdown
     */
    const assignUserToRequest = (name, value) => {
        const requestId = name.replace('assign-request-', '');

        apiRequest('post', 'request/' + requestId + '/assign', {
            data: {
                userId: value
            },
            onSuccess: (response) => {
                toast(toastRef, 'success', 'The case has been ' + (value == 0 ? 'unassigned.' : 'assigned.'));
            },
            onFailure: (error) => {
                toast(toastRef, 'error', 'Error assigning user.');
            }
        });
    };

    /**
     * Assigned to column template
     */
    const ownerBodyTemplate = (data) => {
        if (
            cant('access:supervisor')
            && data?.case?.user_id
            && data.case.user_id != authenticatedUserData.id
        ) {
            return (
                <span>{data.case?.user?.name}</span>
            );
        } else {
            return (
                <DropdownInputTemplate appendTo={null} name={'assign-request-' + data.id}
                                       componentValue={data.case ? (data.case.user_id || 0) : 0}
                                       value={data.case ? (data.case.user_id || 0) : 0}
                                       options={usersOptions}
                                       noMargins
                                       onChangeFunction={assignUserToRequest}
                                       showClear={false}
                                       filter
                />
            );
        }
    };

    /**
     * Actions column template
     */
    const actionsBodyTemplate = (data) => {
        return (
            <div className="actions-column">
                {data.case?.id &&
                    <Button className="p-button-outlined bg-transparent" icon="pi pi-external-link" onClick={() => {
                        setCurrentCaseId(data.case.id);
                        setCurrentRequest(data);
                        setShowCaseModal(true);
                    }}>View</Button>}
            </div>
        );
    };

    /**
     * Get the requests again in case the search, page, rows number or sort has been changed
     */
    useEffect(() => {
        if (!showCaseModal) {
            getRequests();
        }
        clearInterval(intervalVar);
        setIntervalVar(
            setInterval(() => {
                getRequests();
            }, DT_REQUESTS_RELOAD_INTERVAL)
        );
    }, [searchRequestInput, currentPage, sortField, sortOrder, rowsPerPage, currentFilter, showCaseModal]);

    useEffect(() => {
        if (showCaseModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [showCaseModal]);

    useEffect(() => {
        // get users, statuses, chos and GTA groups
        apiRequest('get', 'get-info', {
            data: {query: ['users', 'statuses', 'chos', 'gtagroups'].join(',')},
            onSuccess: (response) => {
                if (response?.data?.responseType == 'success') {
                    if (response.result) {
                        // users
                        if (response.result.users) {
                            const users = [{
                                label: 'Unassigned',
                                value: 0
                            }];

                            if (can('access:supervisor')) {
                                response.result.users.forEach((user) => {
                                    users.push({
                                        label: user.name,
                                        value: user.id
                                    });
                                });
                            } else {
                                response.result.users.forEach((user) => {
                                    if (authenticatedUserData.id == user.id) {
                                        users.push({
                                            label: user.name,
                                            value: user.id
                                        });
                                    }
                                });
                            }

                            setUsersOptions(users);
                        }

                        // statuses
                        if (response.result.statuses) {
                            const statuses = [];

                            response.result.statuses.forEach((status) => {
                                statuses.push({
                                    label: status.label,
                                    value: status.id
                                });
                            });

                            setStatusOptions(statuses);
                        }

                        // chos
                        if (response.result.chos) {
                            const chos = [];

                            response.result.chos.forEach((cho) => {
                                chos.push({
                                    label: cho.name,
                                    value: cho.id
                                });
                            });

                            setChosOptions(chos);
                        }

                        // GTA groups
                        if (response.result.gtagroups) {
                            const gtaGroups = [];

                            response.result.gtagroups.forEach((cho) => {
                                gtaGroups.push({
                                    label: cho.name,
                                    value: cho.name
                                });
                            });

                            setGtaGroupsOptions(gtaGroups);
                        }
                    }
                }
            },
            onFailure: () => {
            }
        });
    }, []);

    /**
     * Clear the debounce on component unmount
     */
    useEffect(() => {
        return () => {
            debounceRequestSearch.cancel();
        };
    });

    return (
        <RequestsContext.Provider value={{statusOptions, chosOptions, gtaGroupsOptions}}>
            <DatatableTemplate
                items={requests}
                header={header}
                rowsPerPage={rowsPerPage}
                loading={loading}
                selectedItems={selectedRequests}
                setSelectedItems={setSelectedRequests}
                emptyMessage="No requests found."
                totalRecords={totalRecords}
                first={first}
                onPageChange={onPageChange}
                customProps={{
                    className: 'requests-datatable',
                    sortField: sortField,
                    sortOrder: sortOrder,
                    onSort: onSort
                }}
            >
                <Column className="column-date-in-business" field="request_date" header="Date in Business" sortable
                        body={(data) => dateBodyTemplate(data.request_date)}/>
                <Column className="column-status" field="status" header="Status" sortable
                        body={(data) => statusBodyTemplate(data?.status)}/>
                <Column className="column-last-status-change" field="latest_status_changed_at" header="Latest Update"
                        sortable
                        key={'column-last-status-change-' + Math.random()}
                        body={(data) => dateBodyTemplate(data.latest_status_changed_at, false, getStatusChangedTooltipMessage(data.latest_status_log, data.latest_status_changed_at))}/>
                <Column className="column-next-action" field="next_action" header="Next Action" sortable
                        body={(data) => statusBodyTemplate(data?.next_action_status2)}/>
                <Column className="column-next-action-date" field="next_action_date" header="Next Action Date" sortable
                        body={(data) => data?.next_action_status ? dateBodyTemplate(data.next_action_date, true) : null}/>
                <Column className="column-sla" field="cho.sla" header="SLA" sortable body={slaBodyTemplate}/>
                <Column className="column-claim-no" field="claims_reference_number" header="Claim No" sortable/>
                <Column className="column-cho" field="cho.name" header="CHO" sortable/>
                <Column className="column-client" field="client_name" header="Client" sortable/>
                <Column className="column-owner" field="case.user.name" header="Owner" sortable
                        body={ownerBodyTemplate}/>
                <Column className="column-actions" body={actionsBodyTemplate}/>
            </DatatableTemplate>

            <CaseModal visible={showCaseModal} setVisible={setShowCaseModal} caseId={currentCaseId}
                       setCaseId={setCurrentCaseId} currentRequest={currentRequest}/>
        </RequestsContext.Provider>
    );
};

export default RequestsDatatable;
